import { Check, ClipboardCopy, Loader2, X } from "lucide-react";
import { useState } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";
import type { HomeFeedLogDetail, HomeFeedLogItem } from "@/types";
import { compactTime } from "@/utils/format";
import { feedTypeLabel, homeFeedCaseText, isDerived } from "@/utils/engagement";

export function HomeFeedModal({
  item,
  detail,
  loading,
  error,
  open,
  onClose,
}: {
  item?: HomeFeedLogItem;
  detail?: HomeFeedLogDetail;
  loading: boolean;
  error: string;
  open: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  if (!item) return null;

  const conversion = item.conversion;
  const source = detail ?? item;

  const copyCase = async () => {
    await navigator.clipboard.writeText(homeFeedCaseText(source));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="grid max-h-[calc(100vh-56px)] w-[min(940px,calc(100vw-56px))] max-w-none grid-rows-[auto_1fr] gap-0 overflow-hidden rounded-[10px] border border-border bg-card p-0 text-[length:inherit] text-foreground shadow-modal ring-0 sm:max-w-none"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5.5 py-4.5">
          <div className="min-w-0">
            <DialogTitle className="mb-1 text-[22px] font-bold">
              {feedTypeLabel(item.type)}
            </DialogTitle>
            <DialogDescription className="text-[length:inherit] text-muted-foreground">
              {item.email} · {item.session.display_name || "세션 미상"} ·{" "}
              {item._environment} · {compactTime(item.generated_at)}
            </DialogDescription>
            <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
              item: {item.home_feed_item_id}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" onClick={copyCase}>
              {copied ? <Check /> : <ClipboardCopy />}
              {copied ? "복사됨" : "사례 복사"}
            </Button>
            <DialogClose render={<Button variant="outline" size="icon" aria-label="닫기" title="닫기" />}>
              <X />
            </DialogClose>
          </div>
        </div>

        <div className="min-w-0 overflow-x-hidden overflow-y-auto px-5.5 pt-4.5 pb-6">
          {error && <Banner tone="error">{error}</Banner>}

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Badge>{item.state}</Badge>
            {item.run.trigger && <Badge size="compact">{item.run.trigger}</Badge>}
            {item.run.model && <Badge size="compact">{item.run.model}</Badge>}
          </div>

          <Card className="mb-4 p-4">
            <div className="mb-2 text-sm font-medium">카드 내용</div>
            <p className="whitespace-pre-wrap">
              {item.text || item.title || "(내용 없음)"}
            </p>
            {item.snippet && (
              <p className="mt-2 text-sm text-muted-foreground">{item.snippet}</p>
            )}
          </Card>

          {conversion && (
            <Card className="mb-4 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                전환 메시지
                {isDerived(conversion.source) && (
                  <Badge
                    size="compact"
                    title="전송 이벤트에 메시지 ID가 없어 시간 기반으로 추정했습니다."
                  >
                    추정
                  </Badge>
                )}
              </div>
              <p className="whitespace-pre-wrap">
                {conversion.text || `(본문 없음 · ${conversion.message_id})`}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                이후 24시간 동안 {conversion.turn_count}턴 이어짐
              </p>
              {isDerived(conversion.source) && (
                <p className="mt-2 text-xs text-muted-foreground">
                  전송 시각 전후 짧은 구간에서 찾은 첫 사용자 메시지입니다. 앱이
                  메시지 ID를 함께 보내기 시작하면 이 추정은 사라집니다.
                </p>
              )}
            </Card>
          )}

          {loading && (
            <p className="mb-4">
              <Loader2 className="mr-1.5 inline-block size-4 animate-spin align-[-3px]" />
              이벤트를 불러오는 중...
            </p>
          )}

          {detail && (
            <Card className="p-4">
              <div className="mb-2 text-sm font-medium">이벤트 타임라인</div>
              {detail.event_timeline.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  기록된 이벤트가 없습니다. 노출은 클라이언트가 보고하므로 앱이 카드를
                  받기 전에 종료되면 아무것도 남지 않습니다.
                </p>
              ) : (
                <div className="grid gap-1.5">
                  {detail.event_timeline.map((event, index) => (
                    <div
                      key={`${event.kind}-${event.created_at}-${index}`}
                      className="grid grid-cols-[140px_170px_1fr] items-center gap-3 border-b border-border py-1.5 text-sm last:border-b-0"
                    >
                      <Badge size="compact">{event.kind}</Badge>
                      <span className="text-muted-foreground">
                        {compactTime(event.created_at)}
                      </span>
                      <span className="min-w-0 truncate font-mono text-xs text-muted-foreground">
                        {event.message_id || ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
